import { notification } from 'antd'
import { action, computed, makeObservable, observable } from 'mobx'

import {
  clearLocalStorageData,
  getLocalStorageData,
  setLocalStorageData,
  STORAGE_KEY,
} from 'services/localStorage.service'
import { userApi } from 'sections/LKSection/modules/UserModule/api'
import {
  CompanyDTO,
  OWNER_STATUS,
  PASSPORT_STATUSES,
  User,
  UserInfoDTO,
} from 'sections/LKSection/modules/UserModule/types'
import { uuid4 } from 'utils/helpers/uuid4'
import { Country } from 'shared/types/types'

import { IRootStore } from '../../store/types'

export class AppStore {
  @observable user: User
  @observable deviceToken: string = ''
  @observable selectedCountry: Country = Country.RU
  @observable company: CompanyDTO | null = null
  @observable isLoadingUserInfo: boolean = false
  @observable isLoadingApp: boolean = true

  constructor(private readonly rootStore: IRootStore) {
    makeObservable(this)
    this.user = this.getUserDefault()
  }

  @computed
  get hasEntity() {
    return this.user.info.owner === OWNER_STATUS.ROLE_ACCEPTED
  }

  @computed
  get needRegistration() {
    const { user_id, owner } = this.user.info || {}

    return user_id !== 0 && owner === OWNER_STATUS.ROLE_NONE
  }

  @computed
  get companyCountry() {
    return this.company?.country ?? Country.RU
  }

  getDeviceUuid(): string {
    let deviceId: string = getLocalStorageData(STORAGE_KEY.DEVICE_UUID)

    if (!deviceId) {
      deviceId = uuid4()
      setLocalStorageData(STORAGE_KEY.DEVICE_UUID, deviceId)
    }

    return deviceId
  }

  @action
  setSelectedCountry = (value: Country) => {
    this.selectedCountry = value
  }

  @action
  setCompany = (value: CompanyDTO) => {
    this.company = value
  }

  @action
  setLoadingApp = (value: boolean) => {
    this.isLoadingApp = value
  }

  @action
  getCompany = async () => {
    try {
      const data = await userApi.getCompanies()
      this.setCompany(data[0])
    } catch (error) {
      notification.error({
        message: 'Ошибка получения списка юрлиц',
        description: (error as Error).message,
      })
    }
  }

  @action
  setLoadingUserInfo = (value: boolean) => {
    this.isLoadingUserInfo = value
  }

  @action
  resetState(): void {
    this.user = this.getUserDefault()
  }

  @action
  setUserInfo = (info: UserInfoDTO) => {
    const { country, additional_phone_country, middle_name, name } = info
    let countryTmp = country
    let additionalPhoneCountryTmp = additional_phone_country
    if (!countryTmp) {
      countryTmp = Country.RU
    }

    if (!additionalPhoneCountryTmp) {
      additionalPhoneCountryTmp = Country.RU
    }

    this.user.info = {
      ...info,
      country: countryTmp,
      additional_phone_country: additionalPhoneCountryTmp,
      middle_name: middle_name.trim(),
      name: name.trim(),
    }
  }

  @action
  setOwnerData = (
    data: Partial<
      Pick<
        UserInfoDTO,
        | 'first_name'
        | 'last_name'
        | 'middle_name'
        | 'email'
        | 'snils'
        | 'additional_phone'
        | 'additional_phone_country'
        | 'telegram_login'
      >
    >,
  ) => {
    Object.assign(this.user.info, data)
  }

  @action
  setAuthenticated = (value: boolean) => {
    this.user.authenticated = value
  }

  @action
  async bootstrap() {
    const token = getLocalStorageData(STORAGE_KEY.ACCESS_TOKEN)

    if (!token) {
      return
    }

    const [userInfo] = await Promise.all([
      this.loadUserData(),
      this.getCompany(),
      this.rootStore.offerStore.apiStore.getOfferVersion(),
    ])

    setLocalStorageData(STORAGE_KEY.USER_INFO, userInfo)
  }

  @action
  loadUserData = async () => {
    try {
      this.setLoadingUserInfo(true)

      const { data: userInfo } = await userApi.getUser()

      this.setUserInfo(userInfo)
      this.setAuthenticated(true)

      return userInfo
    } catch (error) {
      notification.error({
        message: 'Ошибка получения данных пользователя',
        description: (error as Error).message,
      })
    } finally {
      this.setLoadingUserInfo(false)
    }
  }

  @action
  async logout() {
    this.setAuthenticated(false)

    clearLocalStorageData(STORAGE_KEY.ACCESS_TOKEN)
    clearLocalStorageData(STORAGE_KEY.REFRESH_TOKEN)
    clearLocalStorageData(STORAGE_KEY.USER_INFO)
    clearLocalStorageData(STORAGE_KEY.AUTHENTICATED)
    clearLocalStorageData(STORAGE_KEY.COORDINATES)
    clearLocalStorageData(STORAGE_KEY.PICK_POINT_TYPE)
    clearLocalStorageData(STORAGE_KEY.SUBSIDY_HEX_TYPE)

    this.resetState()
  }

  private getUserDefault() {
    return {
      authenticated: false,
      token: '',
      cookie: '',
      info: {
        name: '',
        first_name: '',
        last_name: '',
        middle_name: '',
        country: Country.RU,
        email: '',
        owner: OWNER_STATUS.ROLE_NONE,
        phone: 0,
        user_id: 0,
        roles: [],
        last_push_id: 0,
        snils: 0,
        additional_phone: 0,
        additional_phone_country: Country.RU,
        telegram_login: '',
        passport_approve_status: PASSPORT_STATUSES.none,
      },
    } as User
  }
}
